/**
 * The "what the package includes" bullet editor, shown instead of the
 * entitlement matrix when the package belongs to a display-only service. Those
 * packages gate nothing - they are a price and a list of promises on the public
 * pricing page - so the list IS the content and there is nothing else to edit.
 *
 * One locale at a time: the lines belong to whichever content language the
 * editor's `LangSwitch` is on, and the hook swaps the array underneath.
 */
import { Plus, Trash2 } from "lucide-react";
import type { PlansT } from "@wa/ui";
import { Btn, IconBtn } from "../../../ui";
import { Pill } from "../_shared/LocaleFields";

export function HighlightsEditor({
  lines,
  onUpdate,
  onRemove,
  onAdd,
  t,
}: {
  lines: string[];
  onUpdate: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  t: PlansT;
}) {
  return (
    <div className="space-y-2">
      {lines.map((line, i) => (
        // Keyed by position because a bullet is a bare string with no id of its
        // own. The cost is real but small: removing a line hands its DOM node to
        // the line that moves up, so focus lands on the next bullet rather than
        // being lost. Giving the lines ids would mean changing the shared hook's
        // shape for every app that already reads it.
        <div key={i} className="flex items-center gap-2">
          <Pill
            className="min-w-0 flex-1"
            value={line}
            onChange={(v) => onUpdate(i, v)}
            placeholder={t("editor.highlightPlaceholder")}
          />
          <IconBtn onClick={() => onRemove(i)} aria-label={t("editor.highlightRemove")} title={t("editor.highlightRemove")}>
            <Trash2 className="size-4" />
          </IconBtn>
        </div>
      ))}
      <Btn small onClick={onAdd}>
        <Plus className="size-4" />
        {t("editor.highlightAdd")}
      </Btn>
    </div>
  );
}
